import { motion } from "framer-motion";
import { RotateCcw, Copy, Check, ExternalLink } from "lucide-react"; // Added ExternalLink
import HeadlineCard from "@/components/HeadlineCard";
import KeepDropList from "@/components/KeepDropList";
import NextMoveCard from "@/components/NextMoveCard";
import CategoryCheatSheet from "@/components/CategoryCheatSheet";
import { useState } from "react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_yield-optimizer-9/artifacts/l6st2nus_Siftcard_logo-removebg-preview%20%281%29.png";

// --- YOUR LIVE AFFILIATE REGISTRY ---
const CARD_OFFERS = {
  "Amex": "https://americanexpress.com/en-in/referral/platinum-reserve?ref=mOHAmIr8DG&XLINK=MYCP",
  "IDFC": "https://idfcfr.in/IDFCFB/AGGohc",
  "HDFC": "https://1.hdfc.bank.in/HDFCBK/K/DUvfZXqVlyO",
  "Axis": "https://web.axis.bank.in/DigitalChannel/WebForm/?ipa46&axisreferralcode=emAD0503"
};

export default function ResultsDashboard({ result, onReset }) {
  const [copied, setCopied] = useState(false);

  // Helper to find the referral link based on the card name recommended by AI
  const getReferralLink = (cardName) => {
    if (!cardName) return null;
    const name = cardName.toLowerCase();
    if (name.includes("amex") || name.includes("american express")) return CARD_OFFERS.Amex;
    if (name.includes("idfc")) return CARD_OFFERS.IDFC;
    if (name.includes("hdfc")) return CARD_OFFERS.HDFC;
    if (name.includes("axis")) return CARD_OFFERS.Axis;
    return null;
  };

  const referralLink = getReferralLink(result.next_move?.card_name);

  const generateSummaryText = () => {
    let text = `SiftCard Portfolio Audit\n${"=".repeat(30)}\n\n`;
    text += `Potential Yearly Gain: ${result.headline_savings}\n`;
    text += `Efficiency Score: ${result.efficiency_score}/100\n`;
    text += `Total Annual Fees: ₹${result.total_annual_fees?.toLocaleString("en-IN")}\n`;
    text += `Total Yearly Rewards: ₹${result.total_yearly_rewards?.toLocaleString("en-IN")}\n\n`;

    text += `Card Analysis:\n${"-".repeat(20)}\n`;
    result.cards?.forEach((card) => {
      text += `${card.status} | ${card.name} — ${card.reason}\n`;
      text += `  Fee: ₹${card.annual_fee?.toLocaleString("en-IN")} | Rewards: ₹${card.yearly_rewards?.toLocaleString("en-IN")} | Gain: ₹${card.yearly_gain?.toLocaleString("en-IN")}\n\n`;
    });

    if (result.next_move) {
      text += `Next Move: ${result.next_move.card_name}\n`;
      text += `${result.next_move.why} (${result.next_move.potential_gain})\n\n`;
    }

    text += `Category Cheat Sheet:\n${"-".repeat(20)}\n`;
    result.categories?.forEach((cat) => {
      text += `${cat.category}: ${cat.best_card}\n`;
    });

    return text;
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(generateSummaryText());
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      const el = document.createElement("textarea");
      el.value = generateSummaryText();
      document.body.appendChild(el);
      el.select();
      document.execCommand("copy");
      document.body.removeChild(el);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div className="min-h-screen circuit-bg bokeh-bg relative" data-testid="results-dashboard">
      <div className="grain-overlay" />

      {/* Nav */}
      <nav className="glass-nav w-full px-6 md:px-12 py-4 flex items-center justify-between sticky top-0 z-50">
        <div className="flex items-center">
          <img src={LOGO_URL} alt="SiftCard" className="h-10 sm:h-12 object-contain" style={{ filter: 'brightness(2.2) contrast(1.1)' }} />
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={handleCopy}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white/60 glass-card rounded-full transition-all hover:text-white/80"
            data-testid="copy-summary-btn"
          >
            {copied ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
            {copied ? "Copied!" : "Copy Summary"}
          </button>
          <button
            onClick={onReset}
            className="btn-premium flex items-center gap-2 px-4 py-2 text-sm font-medium text-white rounded-full transition-all"
            data-testid="new-strategy-btn"
          >
            <RotateCcw className="w-4 h-4" />
            New Strategy
          </button>
        </div>
      </nav>

      {/* Dashboard Content */}
      <div className="max-w-6xl mx-auto px-6 md:px-12 py-8 md:py-12 relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-5">
          
          {/* Headline Card */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="md:col-span-2"
            data-testid="result-card-headline"
          >
            <HeadlineCard
              savings={result.headline_savings}
              subtitle={result.headline_subtitle}
              score={result.efficiency_score}
              totalFees={result.total_annual_fees}
              totalRewards={result.total_yearly_rewards}
            />
          </motion.div>

          {/* Next Move Card - NOW PASSING THE REFERRAL LINK */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
            className="md:col-span-2"
            data-testid="result-card-next-move"
          >
            <NextMoveCard 
              nextMove={result.next_move} 
              referralLink={referralLink} 
            />
          </motion.div>

          {/* Keep/Drop List */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
            className="md:col-span-2 md:row-span-2"
            data-testid="result-card-keep-drop"
          >
            <KeepDropList cards={result.cards} />
          </motion.div>

          {/* Category Cheat Sheet */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="md:col-span-2"
            data-testid="result-card-categories"
          >
            <CategoryCheatSheet categories={result.categories} />
          </motion.div>
        </div>

        {/* SUPPORT BANNER (New) */}
        {referralLink && (
          <motion.div 
            initial={{ opacity: 0 }} 
            animate={{ opacity: 1 }} 
            transition={{ delay: 0.5 }}
            className="mt-8 p-4 bg-white/5 border border-white/10 rounded-2xl text-center"
          >
            <p className="text-[10px] text-white/30 uppercase tracking-[0.2em] mb-2 font-bold">Director's Recommendation</p>
            <p className="text-sm text-white/60 mb-3">Apply via SiftCard to unlock Pro features & support unbiased audits.</p>
            <a 
              href={referralLink} 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-emerald-400 font-bold hover:underline"
            >
              Secure Application Portal <ExternalLink className="w-3 h-3" />
            </a>
          </motion.div>
        )}

        {/* Disclaimer */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6 }}
          className="mt-10 text-center text-xs text-white/20 max-w-xl mx-auto leading-relaxed"
          data-testid="disclaimer"
        >
          This analysis is AI-generated based on estimated card benefits and
          your stated spending. Actual rewards may vary. Always verify with your
          card issuer before making changes.
        </motion.p>
      </div>
    </div>
  );
}
